// Scoped realtime subscription for meet. Different pages need different
// table+filter combinations; passing { tables, roomId } keeps each
// subscription tight so the home page doesn't receive every utterance from
// every active call across the org.
//
// Module-level subscription handle (NOT root state) — DOMQL may create a
// fresh state object on update, so direct property assignments
// (root._meetRealtimeChannel) get lost on the next update and every
// callback fires against the dead state object. Module-level vars stay
// stable across state updates.
//
// All event callbacks re-read `this.state.root` freshly via the captured
// `self` element reference so updates always land on the live root state,
// not a stale snapshot.
//
// Named distinctly from chat's subscribeRealtime to avoid function-registry
// collision. Declared as a regular `function` (not arrow) so this-binding
// from el.call(...) works.
//
// migrated 2026-04-27 to SDK realtime — `workspaceProject.realtime.meet`
// dispatches through the SDK's transport-agnostic provider with a
// per-page `tables` selector. The provider tags each callback by table
// kind ('room'|'participant'|'waiting'|'transcript'|'analysis') so the
// dispatch table below routes events without re-reading the table name.

import { getSDK } from '../../../shared/functions/sdk.js'
import { rowToRoom, rowToParticipant, rowToUtterance } from './meetUtils.js'

let _meetUnsub = null
let _meetScope = null

const _rowToWaitingGuest = (r) => ({
  id: r.id,
  roomId: r.room_id,
  guestName: r.guest_name || 'Guest',
  guestEmail: r.guest_email || null,
  requestedAt: r.requested_at,
  admittedAt: r.admitted_at,
  rejectedAt: r.rejected_at
})

// Map page-level table names to provider-level table kinds.
const _tablesToKinds = (tables) => {
  const map = {
    meet_rooms: 'rooms',
    meet_participants: 'participants',
    meet_waiting_room: 'waiting',
    meet_transcripts: 'transcripts',
    meet_transcript_analyses: 'analyses',
  }
  return tables.map((t) => map[t]).filter(Boolean)
}

export const subscribeMeetRealtime = function (opts = {}) {
  const self = this // stable element reference
  const { tables = ['meet_rooms'], roomId = null } = opts

  const scopeKey = `${[...tables].sort().join(',')}:${roomId || '*'}`
  if (_meetScope === scopeKey) return _meetUnsub
  if (_meetUnsub) {
    try { _meetUnsub() } catch (_) {}
    _meetUnsub = null
  }
  _meetScope = scopeKey

  const kinds = _tablesToKinds(tables)
  if (!kinds.length) return null

  let _resolved = null
  let _cancelled = false

  Promise.resolve(getSDK().getService?.('workspaceProject'))
    .then((wp) => {
      if (_cancelled || !wp?.realtime?.subscribeMeet) return
      _resolved = wp.realtime.subscribeMeet({ roomId, tables: kinds }, (kind, payload) => {
        const root = self.state.root

        if (kind === 'room') {
          const next = rowToRoom(payload.new || payload.old || {})
          const cur = root.rooms || []
          if (payload.eventType === 'INSERT') {
            if (!cur.some((r) => r.id === next.id)) root.update({ rooms: [next, ...cur] })
          } else if (payload.eventType === 'UPDATE') {
            root.update({ rooms: cur.map((r) => (r.id === next.id ? next : r)) })
          } else if (payload.eventType === 'DELETE') {
            root.update({ rooms: cur.filter((r) => r.id !== next.id) })
          }
          return
        }

        if (kind === 'participant') {
          const p = rowToParticipant(payload.new || payload.old || {})
          const map = { ...(root.liveParticipants || {}) }
          const list = map[p.roomId] || []
          if (payload.eventType === 'INSERT') {
            if (!list.some((x) => x.id === p.id)) map[p.roomId] = [...list, p]
          } else if (payload.eventType === 'UPDATE') {
            map[p.roomId] = list.map((x) => (x.id === p.id ? p : x))
          } else if (payload.eventType === 'DELETE') {
            map[p.roomId] = list.filter((x) => x.id !== p.id)
          }
          root.update({ liveParticipants: map })
          return
        }

        if (kind === 'transcript') {
          const u = rowToUtterance(payload.new || {})
          const cur = root.utterances || []
          root.update({ utterances: [...cur, u] })
          return
        }

        if (kind === 'analysis') {
          if (payload.new) root.update({ analysis: payload.new })
          return
        }

        if (kind === 'waiting') {
          const g = _rowToWaitingGuest(payload.new || payload.old || {})
          const map = { ...(root.waitingRoom || {}) }
          const list = map[g.roomId] || []
          if (payload.eventType === 'INSERT') {
            if (!list.some((x) => x.id === g.id)) map[g.roomId] = [...list, g]
          } else if (payload.eventType === 'UPDATE') {
            map[g.roomId] = list.map((x) => (x.id === g.id ? g : x))
          } else if (payload.eventType === 'DELETE') {
            map[g.roomId] = list.filter((x) => x.id !== g.id)
          }
          root.update({ waitingRoom: map })
        }
      })
    })
    .catch((err) => {
      console.warn('[meet] subscribeMeetRealtime failed:', err?.message || err)
    })

  _meetUnsub = () => {
    _cancelled = true
    if (typeof _resolved === 'function') {
      try { _resolved() } catch (_) {}
    }
    _meetScope = null
  }
  return _meetUnsub
}
